import AgoraRTC, {
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from "agora-rtc-sdk-ng";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Col, Container, Form, Row } from "react-bootstrap";
import useXoltarNavigation from "../../hooks/useXoltarNav";
import { useCoreAvailability } from "../../hooks/validateCores";
import { useWebValidation } from "../../hooks/websiteValidatioHook";
import BaseCardView from "../../Components/BaseCardView/BaseCardView";
import { useAppDispatch, useAppSelector } from "../../redux-stores/hooks/hooks";
import {
  selectAvailableCore,
  selectConvDevices,
  setCameraId,
  setMicrophoneId,
} from "../../redux-stores/reducers/ConversationState";
import { selectQuestionnaireValidStatus } from "../../redux-stores/reducers/QuestionerState";
import { addToast } from "../../redux-stores/reducers/ToastState";

function AudioBar(props: {
  stream: IMicrophoneAudioTrack;
  delta: number;
  onValid?: (valid: boolean) => void;
  onInvalid?: (valid: boolean) => void;
}) {
  const { stream, delta = 15, onValid, onInvalid } = props;
  const [loaderValue, setLoaderValue] = useState(0);
  const [valid, setValid] = useState(false);
  const audioFrameCallback = useCallback(() => {
    const vol = stream.getVolumeLevel() * 100;
    setLoaderValue(vol);
    if (vol > delta) {
      !valid && setValid(true);
      onValid && onValid(vol > delta);
    } else if (vol === 0) {
      onInvalid && onInvalid(false);
      valid && setValid(false);
    }
  }, [stream, valid, delta, onInvalid, onValid]);
  useEffect(() => {
    stream.setAudioFrameCallback((buff) => {
      audioFrameCallback();
    }, 1024);
    return () => {};
  }, [stream, audioFrameCallback]);
  return (
    <Container>
      <Row>
        <Container
          style={{
            display: stream ? "flex" : "none",
            flexDirection: "row",
          }}
        >
          <div
            style={{
              width: `${
                (Math.min(delta, Math.max(loaderValue, 1)) * 100) / (delta * 3)
              }%`,
              height: "1.25rem",
              backgroundColor: "red",
              margin: 0,
              padding: 0,
              borderRadius: "0.25rem",
            }}
          ></div>
          <div
            style={{
              width: `${
                (Math.min(delta, Math.max(loaderValue - delta, 0)) * 100) /
                (delta * 3)
              }%`,
              height: "1.25rem",
              backgroundColor: "orange",
              margin: 0,
              padding: 0,
              borderRadius: "0.25rem",
            }}
          ></div>
          <div
            style={{
              width: `${
                (Math.min(delta, Math.max(loaderValue - delta * 2, 0)) * 100) /
                (delta * 3)
              }%`,
              height: "1.25rem",
              backgroundColor: "green",
              margin: 0,
              padding: 0,
              borderRadius: "0.25rem",
            }}
          ></div>
        </Container>
      </Row>
      <Row>
        {loaderValue < delta && !valid && <div>Microphone volume to low</div>}
      </Row>
    </Container>
  );
}

export default function PreConvTest(props: {}) {
  const isQuestionnaireValid = useAppSelector(selectQuestionnaireValidStatus);
  const storedDevices = useAppSelector(selectConvDevices);
  const haveCores = useAppSelector(selectAvailableCore);
  const dispatch = useAppDispatch();
  useWebValidation();
  const checkForCores = useCoreAvailability(10_000);

  const navigation = useXoltarNavigation();
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevice, setAudioDevice] = useState<IMicrophoneAudioTrack | null>(
    null
  );
  const [cameraDevice, setCameraDevice] = useState<ICameraVideoTrack | null>(
    null
  );
  const [error, setError] = useState({
    showError: false,
    errorMsg: "",
  });
  const [areDevicesEnabled, setAreDevicesEnabled] = useState({
    mic: false,
    cam: false,
  });
  const [loadingDevices, setLoadingDevices] = useState(true);
  const videoChangeCallback = useCallback(
    (value: string) => {
      setAreDevicesEnabled({
        ...areDevicesEnabled,
        cam: false,
      });
      cameraDevice && cameraDevice.stop();
      if (videoDiv.current) {
        videoDiv.current.innerHTML = "";
      }
      if (cameraDevice && value === "-1") {
        cameraDevice.close();
        setAreDevicesEnabled({
          ...areDevicesEnabled,
          cam: false,
        });
        setCameraDevice(null);
      } else {
        dispatch(setCameraId(value));
        AgoraRTC.createCameraVideoTrack({
          cameraId: value,
        })
          .then((stream) => {
            setCameraDevice(stream);
            if (videoDiv.current) {
              stream.play(videoDiv.current, {
                fit: "contain",
              });
              const nodes = videoDiv.current.childNodes;
              for (let i = 0; i < nodes.length; i++) {
                const element = nodes[i];
                if (element.nodeName.toLowerCase() === "div") {
                  (element as any).style.background = "transparent";
                }
              }
            }
            if (!areDevicesEnabled.cam)
              setAreDevicesEnabled({
                ...areDevicesEnabled,
                cam: true,
              });
          })
          .catch((e) => {
            dispatch(
              addToast({
                bg: "Danger",
                body: `${e}`,
                header: "Error on cam",
                duration: 4_500,
                timestamp: Date.now(),
              })
            );
            dispatch(setCameraId(""));
          });
      }
    },
    // eslint-disable-next-line
    [cameraDevice]
  );
  const audioChangeCallback = useCallback(
    (value: string) => {
      if (audioDevice) {
        audioDevice.setAudioFrameCallback(() => {});
        audioDevice.removeAllListeners();
        audioDevice.stop();
        audioDevice.close();
        setAudioDevice(null);
        dispatch(setMicrophoneId(""));
      }
      if (value !== "-1") {
        console.debug("Audio device is set", value);
        dispatch(setMicrophoneId(value));
        AgoraRTC.createMicrophoneAudioTrack({
          microphoneId: value,
        })
          .then((device) => {
            setAudioDevice(device);
            if (!device) return;
            const _device = devices.find((d) => d.deviceId === value);
            dispatch(
              addToast({
                bg: "Info",
                body: `Changed mic device ${_device?.label}`,
                header: "Device changed",
                duration: 4_500,
                timestamp: Date.now(),
              })
            );
          })
          .catch((e) => {
            dispatch(
              addToast({
                bg: "Danger",
                body: `${e}`,
                header: "Error on mic",
                duration: 4_500,
                timestamp: Date.now(),
              })
            );
            dispatch(setMicrophoneId(""));
          });
      }
    },
    // eslint-disable-next-line
    [audioDevice]
  );
  const closeDeviceCallback = React.useCallback(() => {
    audioDevice?.close();
    cameraDevice?.close();
    console.debug("Device close was called");
  }, [audioDevice, cameraDevice]);
  const videoDiv = useRef<HTMLDivElement>(null);
  useEffect(() => {
    AgoraRTC.getDevices()
      .then((devices) => {
        setDevices(devices);
      })
      .catch(() => {
        setError({
          errorMsg: "Unable to get input devices",
          showError: true,
        });
      })
      .finally(() => {
        setLoadingDevices(false);
      });
  }, []);
  useEffect(() => {
    if (!audioDevice && !cameraDevice) {
      const deviceVideo = devices.filter((d) => d.kind === "videoinput").at(0);
      if (deviceVideo && deviceVideo?.deviceId) {
        dispatch(setCameraId(deviceVideo.deviceId));
        videoChangeCallback(deviceVideo.deviceId);
      }
      const deviceAudio = devices.filter((d) => d.kind === "audioinput").at(0);
      if (deviceAudio && deviceAudio?.deviceId) {
        dispatch(setMicrophoneId(deviceAudio.deviceId));
        audioChangeCallback(deviceAudio.deviceId);
      }
    }
    // eslint-disable-next-line
  }, [devices, devices.length]);
  useEffect(() => {
    if (!isQuestionnaireValid) navigation("Intro");
  }, [isQuestionnaireValid, navigation]);

  return (
    <BaseCardView
      title="Hardware test"
      dynamicLoad={false}
      cardStyle={{ width: "100%" }}
      onNext={() => {
        let msg = "";
        if (!areDevicesEnabled.cam) {
          msg = `a camera `;
        }
        if (!areDevicesEnabled.mic) {
          msg += `${msg.length >= 1 ? "and" : "a"} microphone`;
        }
        if (msg.length >= 1) {
          setError({
            errorMsg: "Please choose " + msg,
            showError: true,
          });
          return;
        }
        audioDevice?.setAudioFrameCallback(() => {});
        closeDeviceCallback();
        if (haveCores)
          checkForCores().then((available) => {
            if (available) navigation("LoadingConversation");
            else setError({ errorMsg: "No agents", showError: true });
          });
      }}
      onPrevious={() => {
        closeDeviceCallback();
        navigation("Questions");
      }}
      disableNext={!haveCores}
      nextBtnText={haveCores ? undefined : "No agents"}
      bodyComponent={() => (
        <>
          <Container fluid>
            {error.showError && (
              <Row>
                <Alert
                  dismissible
                  onClose={() => {
                    setError({ errorMsg: "", showError: false });
                  }}
                  variant="danger"
                >
                  {error.errorMsg}
                </Alert>
              </Row>
            )}
            {loadingDevices ? (
              <>
                <Row>Please allow access to camera and microphone</Row>
              </>
            ) : (
              <Row style={{ height: "100%" }}>
                <Col sm={cameraDevice ? "7" : "8"}>
                  <Form.Group>
                    <Form.Label>{"Choose camera"}</Form.Label>
                    <Form.Select
                      value={storedDevices.cam}
                      onChange={(e) => {
                        videoChangeCallback(e.target.value);
                      }}
                    >
                      <option value={"-1"}>Open this select menu</option>
                      {devices
                        .filter((device) => device.kind === "videoinput")
                        .map((device, i) => {
                          return (
                            <option
                              key={device.deviceId}
                              value={device.deviceId}
                            >
                              {device.label.length >= 1
                                ? device.label
                                : `Camera ${i}`}
                            </option>
                          );
                        })}
                    </Form.Select>
                    <Form.Label>Choose microphone</Form.Label>
                    <Form.Select
                      value={storedDevices.mic}
                      onChange={(e) => {
                        setAreDevicesEnabled({
                          ...areDevicesEnabled,
                          mic: false,
                        });
                        audioChangeCallback(e.target.value);
                      }}
                    >
                      <option value={"-1"}>Open this select menu</option>
                      {devices
                        .filter((device) => device.kind === "audioinput")
                        .map((device, i) => {
                          return (
                            <option
                              key={device.deviceId}
                              value={device.deviceId}
                            >
                              {device.label.length >= 1
                                ? device.label
                                : `Microphone ${i}`}
                            </option>
                          );
                        })}
                    </Form.Select>
                  </Form.Group>
                  {audioDevice ? (
                    <AudioBar
                      stream={audioDevice}
                      delta={20}
                      onValid={() => {
                        if (!areDevicesEnabled.mic) {
                          console.debug("Set mic as valid");
                          setAreDevicesEnabled({
                            ...areDevicesEnabled,
                            mic: true,
                          });
                        }
                      }}
                      onInvalid={() => {
                        if (areDevicesEnabled.mic) {
                        }
                      }}
                    />
                  ) : null}
                </Col>
                <Col ref={videoDiv} sm={cameraDevice ? "5" : "1"}></Col>
              </Row>
            )}
          </Container>
        </>
      )}
    />
  );
}
